<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-globe-stand'"
      :section-title="$tc('configuration.sections.countries', 2)"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteCountries(selectedRows)"
        @shortkey="deleteCountries(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCountryModal($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showCountryModal($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showCountryModal($t('configuration.actions.add'))"
        @shortkey="showCountryModal($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="countryModal">
      <country-modal
        :action-title="actionTitle"
        @update="patchCountries($event)"
        @create="createCountry($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsCountriesGrid from '@/modules/configurationModule/detail/demography/countries/columnsCountriesGrid'
import CountryModal from '@/modules/configurationModule/detail/demography/countries/CountryModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import CountryConfigurationServices from '@/api/resources/configuration/demography/country/CountryConfigurationServices'

export default {
    name: 'DemographyCountriesGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        CountryModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.demography.countries',
            actionTitle: null,
            selectedRows: [],
            columnDefs: columnsCountriesGrid(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        msgSelectedRows() {
            let msg = `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}`
            if (this.selectedRows.length === 0) msg = ''
            return msg
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveCountry() {
            return !!this.selectedRows.find(country => country.active)
        },
        hasDeactiveCountry() {
            return !!this.selectedRows.find(country => !country.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(country => country.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(country => !country.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveCountry && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveCountry && !this.hasAllActivated
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const countriesToPatch = selectionBatch.filter(country => country.active !== option).map(country => {
                return {
                    active: option,
                    id: country.id
                }
            })
            if (countriesToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(CountryConfigurationServices.patchCountriesBatch(countriesToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobal()
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchCountries(...countries) {
            const response = await this.$_toCallUnique(CountryConfigurationServices.patchCountriesBatch(countries))
            if (response) {
                this.$refs.countryModal.hide()
                this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated'))
                this.reloadData()
            }
        },
        async createCountry(country) {
            const response = await this.$_toCallUnique(CountryConfigurationServices.createCountry(country))
            if (response) {
                this.$refs.countryModal.hide()
                this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deleteCountriesBatch(countries) {
            const countriesToDelete = countries.map(country => country.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(CountryConfigurationServices.deleteCountriesBatch(countriesToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (countriesToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: countriesToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(CountryConfigurationServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showCountryModal(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 ? ['not-show'] : '',
                        action: () => this.deleteCountries(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(CountryConfigurationServices))
        },
        showCountryModal(title, selectedCountry) {
            this.actionTitle = title
            this.$refs.countryModal.show([selectedCountry])
        },
        async deleteCountries(countries) {
            if (countries.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    this.deleteCountriesBatch(countries)
                }
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
